import head from 'lodash/head';
import { EMBARGO_EXTERNAL_ATTR, IS_PRESELECTED_PARAM_NAME, MASTER_PRODUCT_SKU, POPULARITY_INDEX_ATTR, QueryParams, VARIANTS_COLLECTION, } from '@yme/common/constants';
import { E_COMMERCE_ENABLED_KEY, PRODUCT_YEAR_ATTR } from '@yme/selectors/constants';
import { getAllVariantsForProduct, getGroupingDataVariants, hasGroupingProducts, } from '@yme/selectors/helpers';
import { STUDIO_IMAGE } from '@yme/utils/constants';
export const getProductURL = ({ path, isInAuthor, slug, sku, }) => {
    if (!path) {
        return '';
    }
    let productURLPathname;
    if (isInAuthor) {
        const formattedPath = path?.replace('.html', '');
        productURLPathname = `${formattedPath}.${slug}.html`;
    }
    else {
        productURLPathname = `${path}${slug}/`;
    }
    return sku ? `${productURLPathname}#${sku}` : productURLPathname;
};
export const getPdpLinkByCategory = (pdpLinks, categories) => {
    const publishedCategories = categories.filter((category) => category.published);
    const existingCategories = publishedCategories.length ? publishedCategories : categories;
    const existingCategory = existingCategories.find((category) => {
        const categoryId = category.key.replace(/\D/g, '');
        return pdpLinks[categoryId];
    });
    const categoryKey = existingCategory?.key?.replace(/\D/g, '') || '';
    return pdpLinks[categoryKey];
};
export function getProductSortedByPrice(data) {
    if (!data?.length) {
        return [];
    }
    const finalSortedData = data.slice();
    finalSortedData.sort((a, b) => {
        const pricesA = a.prices.length > 0
            ? a.prices
            : [
                {
                    amount: Infinity,
                },
            ];
        const pricesB = b.prices.length > 0
            ? b.prices
            : [
                {
                    amount: Infinity,
                },
            ];
        const amountA = pricesA[0].amount;
        const amountB = pricesB[0].amount;
        return amountA - amountB;
    });
    return finalSortedData;
}
export const getVariationsVariants = (product) => {
    const groupingVariants = getGroupingDataVariants(product.groupingProducts ?? []);
    return getAllVariantsForProduct(product.variants, groupingVariants);
};
export const filterAvailableVariant = (variant) => {
    const eComEnabled = variant.attributes.find((attribute) => attribute.name === E_COMMERCE_ENABLED_KEY)?.value ??
        false;
    const price = head(variant?.prices);
    return Boolean(eComEnabled && price);
};
export const getVariantWithAvailableLowestPrise = ({ product, isFilterAvailableVariant, }) => {
    const masterVariant = product.variants.find((variant) => variant.master);
    const minPricesArr = [];
    const variants = hasGroupingProducts(product) ? getVariationsVariants(product) : product.variants;
    const variantsResult = isFilterAvailableVariant
        ? variants.filter(filterAvailableVariant)
        : variants;
    const minPrice = variantsResult.reduce((min, item) => {
        const minItemPrice = item.prices.length
            ? head(item.prices)?.discount?.discountedPrice || head(item.prices)?.amount
            : Infinity;
        if (minItemPrice && min > minItemPrice) {
            min = minItemPrice;
        }
        minPricesArr.push(minItemPrice);
        return min;
    }, Infinity);
    const variantWithLowestPrice = variantsResult.find((item) => head(item.prices)?.amount === minPrice ||
        head(item.prices)?.discount?.discountedPrice === minPrice);
    const isLowestPriseExist = variantWithLowestPrice
        ? !minPricesArr.every((item) => minPrice === item)
        : false;
    if (isFilterAvailableVariant) {
        return {
            variantWithLowestPrice,
            isLowestPriseExist,
        };
    }
    return {
        variantWithLowestPrice: !variantWithLowestPrice || !isLowestPriseExist
            ? masterVariant || head(product.variants)
            : variantWithLowestPrice,
        isLowestPriseExist,
    };
};
export function getProductCardData({ itemData, path, isInAuthor, isImageMasterVariant = false, 
// These next additional props for functionality of feature related this ticket - [B2C-19239]
isCheapestVariant = false, }) {
    const masterVariant = itemData.variants.find((item) => Boolean(item.master));
    const { variantWithLowestPrice, isLowestPriseExist } = getVariantWithAvailableLowestPrise({
        product: itemData,
    });
    const cheapestVariant = isCheapestVariant && variantWithLowestPrice;
    // define current variant
    const currentVariant = cheapestVariant || masterVariant;
    // getting images data
    const imagesVariant = isImageMasterVariant ? masterVariant?.images : currentVariant?.images;
    const image = imagesVariant?.find((item) => {
        const preparedUrl = item.url.toLowerCase().indexOf(STUDIO_IMAGE);
        return preparedUrl !== -1 || item.url;
    });
    const finallyImage = (image && [image]) || [];
    // getting all related attributes data of current variant
    const currentAttsVariant = currentVariant?.attributes || [];
    const embargoExternal = currentAttsVariant.find((i) => i?.name === EMBARGO_EXTERNAL_ATTR);
    const popularityIndex = currentAttsVariant.find((i) => i?.name === POPULARITY_INDEX_ATTR);
    const sku = currentVariant?.sku || masterVariant?.sku || '';
    const cardUrl = getProductURL({
        path,
        isInAuthor,
        slug: itemData.slug,
        sku,
    });
    const isStock = itemData.variants.every((item) => Boolean(item.stock > 0));
    return {
        currentVariant,
        images: finallyImage,
        prices: currentVariant?.prices || [],
        name: itemData?.name || '',
        embargoExternal: embargoExternal?.value || 0,
        popularityIndex: popularityIndex?.value || 0,
        stock: currentVariant?.stock,
        isStock,
        isLabelFromVisible: Boolean(isCheapestVariant && isLowestPriseExist),
        cardUrl,
        sku,
        masterVariant,
        type: itemData.type,
    };
}
export const getInRiverId = (categoryKey) => Number(categoryKey.replace(/\D/g, ''));
export const getTestRideUrl = ({ url, sku, dealerId = '', masterProductSku = '', isIframe = false, isPreselected = false, }) => {
    try {
        const urlWithParams = new URL(url, window.location.origin);
        urlWithParams.searchParams.set('model', sku);
        if (dealerId) {
            urlWithParams.searchParams.set('dealerId', dealerId);
        }
        if (isIframe) {
            urlWithParams.searchParams.set('isIframe', `${isIframe}`);
        }
        if (isPreselected) {
            urlWithParams.searchParams.set(IS_PRESELECTED_PARAM_NAME, `${isPreselected}`);
        }
        if (masterProductSku) {
            urlWithParams.searchParams.set(MASTER_PRODUCT_SKU, masterProductSku);
        }
        return `${urlWithParams.pathname}${urlWithParams.search}`;
    }
    catch (error) {
        return '';
    }
};
export const getLatestModelTitle = ({ isLatestModel = false, name, productYear, }) => {
    const year = !isLatestModel ? `${productYear} ` : '';
    return `${year}${name}`;
};
export function getProductYear(productProps) {
    const masterVariant = productProps.variants.find((i) => i.master);
    const productYear = masterVariant?.attributes.find((attribute) => attribute.name === PRODUCT_YEAR_ATTR)?.value;
    return productYear;
}
export const getPLPLinkWithAccessoriesAndApparels = (rootLink, productKey, apparelCollection = '') => {
    if (!rootLink) {
        return '';
    }
    const productIdParam = productKey ? `?${QueryParams.PRODUCT}=${productKey}` : '';
    const collection = apparelCollection ? `&${VARIANTS_COLLECTION}=${apparelCollection}` : '';
    return productIdParam ? `${rootLink}${productIdParam}${collection}` : '';
};
export function getMasterVariantSku(variants = []) {
    return variants.find((variant) => variant.master)?.sku || '';
}
export function getUpperCasedValueForProductType(productType) {
    return productType.toUpperCase().trim().replace(' ', '').replace('-', '');
}
export const getMasterVariant = (variants) => variants?.find((variant) => Boolean(variant.master));
export const getCurrentCategory = (categories) => {
    if (!categories?.length)
        return null;
    return categories.find((category) => category.collection && category.published);
};
